import {
  MessageResponseSchema,
  type MessageResponse,
  ConversationSchema,
  type Conversation,
} from '@voll-intelligence/domain';
import { z } from 'zod';
import type { HttpService, ConversationStreamClient } from '@web/data/services';

const MessagesResponseSchema = z.object({
  data: z.array(MessageResponseSchema),
  nextCursor: z.string().nullable(),
});

export interface ConversationRepository {
  /** Reads messages from the conversation service (cursor-paginated). */
  loadMessages: (
    conversationId: string,
    opts?: { order?: 'asc' | 'desc'; limit?: number; cursor?: string },
  ) => Promise<{ data: MessageResponse[]; nextCursor: string | null }>;
  /**
   * Sends a human reply (POST /conversations/:id/reply).
   *
   * Accepts either the new structured form `{ text, messageId }` (P7.4+) or
   * the legacy plain-string form `text`. Both map to the same endpoint.
   */
  sendReply: (
    conversationId: string,
    body: { text: string; messageId: string } | string,
  ) => Promise<void>;
  /**
   * Subscribes to the live SSE stream for a conversation. Delivers incoming
   * `MessageResponse[]` batches to `onMessages`. Returns an unsubscribe fn
   * that aborts the stream on cleanup.
   */
  subscribeThread: (
    conversationId: string,
    onMessages: (messages: MessageResponse[]) => void,
  ) => () => void;
  /** Reads conversation metadata (GET /conversations/:id) — includes translationMode + customerLanguage. */
  loadMeta: (conversationId: string) => Promise<Conversation>;
  /** Flips the outbound-translation toggle (PUT /conversations/:id/translation). */
  setTranslationMode: (conversationId: string, mode: 'off' | 'on') => Promise<void>;
}

export function createConversationRepository(
  http: HttpService,
  streamClient: ConversationStreamClient,
): ConversationRepository {
  return {
    loadMessages: (conversationId, opts = {}) =>
      http.get(`/conversations/${encodeURIComponent(conversationId)}/messages`, {
        query: {
          order: opts.order ?? 'desc',
          limit: opts.limit ?? 50,
          ...(opts.cursor ? { cursor: opts.cursor } : {}),
        },
        schema: MessagesResponseSchema,
      }),

    sendReply: async (conversationId, body) => {
      const payload = typeof body === 'string' ? { text: body } : body;
      await http.post(`/conversations/${encodeURIComponent(conversationId)}/reply`, payload);
    },

    subscribeThread: (conversationId, onMessages) =>
      streamClient.subscribe(conversationId, onMessages),

    loadMeta: (conversationId) =>
      http.get(`/conversations/${encodeURIComponent(conversationId)}`, {
        schema: ConversationSchema,
      }),

    setTranslationMode: async (conversationId, mode) => {
      await http.put(`/conversations/${encodeURIComponent(conversationId)}/translation`, { mode });
    },
  };
}
